/**
 * Ridalo — Destinations et zones de Thaïlande
 * Structure extensible pour ajouter facilement de nouvelles destinations.
 */

export const DESTINATIONS = {
  phuket: {
    id: 'phuket',
    name: 'Phuket',
    zones: [
      'Patong',
      'Kata',
      'Karon',
      'Kamala',
      'Rawai',
      'Chalong',
      'Phuket Town',
      'Nai Harn',
      'Bangtao',
      'Surin',
      'Mai Khao',
      'Cherngtalay',
      'Toute l\'île de Phuket',
    ],
  },
  krabi: {
    id: 'krabi',
    name: 'Krabi',
    zones: [
      'Ao Nang',
      'Krabi Town',
      'Railay',
      'Klong Muang',
      'Tub Kaek',
      'Toute la zone de Krabi',
    ],
  },
  kohSamui: {
    id: 'kohSamui',
    name: 'Koh Samui',
    zones: [
      'Chaweng',
      'Lamai',
      'Bophut',
      'Maenam',
      'Choeng Mon',
      'Nathon',
      'Toute l\'île de Koh Samui',
    ],
  },
  bangkok: {
    id: 'bangkok',
    name: 'Bangkok',
    zones: [
      'Sukhumvit',
      'Silom',
      'Siam',
      'Khao San',
      'Thonglor',
      'Ekkamai',
      'Sathorn',
      'Ratchada',
      'Toute la ville de Bangkok',
    ],
  },
  pattaya: {
    id: 'pattaya',
    name: 'Pattaya',
    zones: [
      'Central Pattaya',
      'North Pattaya',
      'South Pattaya',
      'Jomtien',
      'Naklua',
      'Toute la zone de Pattaya',
    ],
  },
};

// Array of destinations for dropdowns
export const DESTINATION_LIST = Object.values(DESTINATIONS).map(d => ({
  id: d.id,
  name: d.name,
}));

// Get zones for a specific destination
export const getZonesForDestination = (destinationId) => {
  return DESTINATIONS[destinationId]?.zones || [];
};

// Normalize destination ID (for case-insensitive matching)
export const normalizeDestinationId = (raw) => {
  if (!raw) return '';
  const lower = String(raw).toLowerCase().trim();
  
  // Map common variations to canonical IDs
  const mapping = {
    'phuket': 'phuket',
    'krabi': 'krabi',
    'koh samui': 'kohSamui',
    'kohsamui': 'kohSamui',
    'samui': 'kohSamui',
    'bangkok': 'bangkok',
    'pattaya': 'pattaya',
  };
  
  return mapping[lower] || '';
};

// Infer destination from old location string (for migration)
export const inferDestinationFromLocation = (location) => {
  if (!location) return 'phuket'; // Default for legacy
  
  const lower = String(location).toLowerCase();
  
  // Check if location matches any Phuket zones
  const phuketZones = DESTINATIONS.phuket.zones.map(z => z.toLowerCase());
  if (phuketZones.some(z => lower.includes(z.toLowerCase()) || z.toLowerCase().includes(lower))) {
    return 'phuket';
  }
  
  // Check Krabi
  const krabiZones = DESTINATIONS.krabi.zones.map(z => z.toLowerCase());
  if (krabiZones.some(z => lower.includes(z.toLowerCase()) || z.toLowerCase().includes(lower))) {
    return 'krabi';
  }
  
  // Check Koh Samui
  const samuiZones = DESTINATIONS.kohSamui.zones.map(z => z.toLowerCase());
  if (samuiZones.some(z => lower.includes(z.toLowerCase()) || z.toLowerCase().includes(lower))) {
    return 'kohSamui';
  }
  
  // Check Bangkok
  const bangkokZones = DESTINATIONS.bangkok.zones.map(z => z.toLowerCase());
  if (bangkokZones.some(z => lower.includes(z.toLowerCase()) || z.toLowerCase().includes(lower))) {
    return 'bangkok';
  }
  
  // Check Pattaya
  const pattayaZones = DESTINATIONS.pattaya.zones.map(z => z.toLowerCase());
  if (pattayaZones.some(z => lower.includes(z.toLowerCase()) || z.toLowerCase().includes(lower))) {
    return 'pattaya';
  }
  
  // Default to Phuket for legacy data
  return 'phuket';
};

// Normalize zone name (capitalize properly)
export const normalizeZone = (raw, destinationId) => {
  if (!raw) return '';
  const cleaned = String(raw).trim().replace(/\s+/g, ' ');
  const lower = cleaned.toLowerCase();
  
  // Get canonical zones for the destination
  const zones = getZonesForDestination(destinationId);
  const canonical = zones.find((z) => z.toLowerCase() === lower);
  
  if (canonical) return canonical;
  
  // Title case fallback
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
};
